import { Link, useParams } from "react-router-dom";

import { Btn } from "../../lib/Btn";
import { Card } from "../../lib/Card";
import { Icon } from "../../lib/Icon";
import { Pill } from "../../lib/Pill";
import { useSeo } from "../../lib/useSeo";
import { TOOLS, type ToolVertical } from "./data/tools";

// URL-slug → ToolMeta.vertical key. Marketing surfaces use a friendlier
// slug (rv-and-boat) than the schema enum (vehicle_storage); map both
// directions for /tools/vertical/{slug}.
const SLUG_TO_VERTICAL: Record<string, ToolVertical> = {
  "self-storage": "self_storage",
  multifamily: "multifamily",
  "rv-and-boat": "vehicle_storage",
  parking: "parking",
};

const VERTICAL_LABEL: Record<ToolVertical, string> = {
  multifamily: "Multifamily",
  self_storage: "Self-storage",
  parking: "Parking & lots",
  vehicle_storage: "RV & boat",
};

const VERTICAL_SLUG: Record<ToolVertical, string> = {
  multifamily: "multifamily",
  self_storage: "self-storage",
  parking: "parking",
  vehicle_storage: "rv-and-boat",
};

const VERTICAL_ACCENT: Record<ToolVertical, string> = {
  multifamily: "var(--multifamily-accent, #3F6B4B)",
  self_storage: "var(--storage-accent, #B5543B)",
  parking: "var(--parking-accent, #6C5AA8)",
  vehicle_storage: "var(--vehicle-accent, #2D6E8F)",
};

// Market-size order — same as the rest of the marketing site.
const VERTICAL_ORDER: ToolVertical[] = [
  "multifamily",
  "self_storage",
  "parking",
  "vehicle_storage",
];

const VERTICAL_BLURB: Record<ToolVertical, string> = {
  multifamily:
    "Per-state deposit caps, eviction timelines, fair-housing checklists, and renewal math for apartment, condo, and senior-living operators.",
  self_storage:
    "Late-fee caps, lien-sale timing, unit-size guidance, and per-state sales-tax lookups for self-storage operators.",
  parking:
    "Permit pricing benchmarks, rate-card builders, towing rules, and parking-tax lookups for surface, structured, and monthly-permit operators.",
  vehicle_storage:
    "LOA-based slip pricing, RV-bay size guides, hull-insurance checklists, and per-state marina/RV-storage tax lookups.",
};

export function ToolsIndexPage() {
  const params = useParams();
  const vertical = params.vertical ? SLUG_TO_VERTICAL[params.vertical] : null;
  const label = vertical ? VERTICAL_LABEL[vertical] : "property operators";

  useSeo({
    title: vertical
      ? `Free ${label.toLowerCase()} tools — NestPlot`
      : "Free property operations tools — NestPlot",
    description: vertical
      ? `Free calculators and lookups for ${label.toLowerCase()} operators. Statute-cited per state, no signup required.`
      : "Free calculators and lookups across multifamily, self-storage, parking, and RV/boat. Statute-cited per state, no signup required.",
    path: vertical ? `/tools/vertical/${params.vertical}` : "/tools",
  });

  const filteredTools = vertical
    ? TOOLS.filter((t) => t.vertical === vertical)
    : TOOLS;

  return (
    <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12 lg:py-16">
      <div className="max-w-[760px]">
        <Pill tone="accent" size="sm">
          Free · no signup
        </Pill>
        <h1 className="t-display-xl mt-4">
          {vertical ? `Free tools for ${label.toLowerCase()}.` : "Free tools for property operators."}
        </h1>
        <p className="text-lg text-sub mt-4">
          {filteredTools.length} free calculators and lookups that pull from
          the same per-state data NestPlot uses internally. Every tool works on
          its own or pre-filled for your state — pick a state on the tool page
          or grab a tool from the cards below.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/signup">
            <Btn kind="primary" size="md" iconRight={<Icon name="arrow-right" size={14} />}>
              Start free trial
            </Btn>
          </Link>
          <Link to="/states">
            <Btn kind="ghost" size="md">
              All 50 states →
            </Btn>
          </Link>
        </div>
        {!vertical && (
          <nav className="mt-7 flex flex-wrap gap-2 text-sm">
            {VERTICAL_ORDER.map((v) => (
              <a
                key={v}
                href={`#${VERTICAL_SLUG[v]}`}
                className="border border-line rounded-md px-3 py-1.5 font-semibold hover:bg-paper hover:border-line-strong transition-colors"
              >
                {VERTICAL_LABEL[v]} →
              </a>
            ))}
          </nav>
        )}
      </div>

      <div className="mt-12 space-y-12">
        {vertical ? (
          <SingleVerticalSection
            vertical={vertical}
            tools={filteredTools}
          />
        ) : (
          VERTICAL_ORDER.map((v, idx) => (
            <section
              key={v}
              id={VERTICAL_SLUG[v]}
              className={idx > 0 ? "border-t border-line pt-12" : undefined}
            >
              <div className="grid lg:grid-cols-[1fr_2.5fr] gap-6 items-start">
                <div>
                  <div
                    className="font-mono text-[11px] tracking-[0.18em] uppercase"
                    style={{ color: VERTICAL_ACCENT[v] }}
                  >
                    Vertical · {VERTICAL_LABEL[v]}
                  </div>
                  <h2 className="font-display font-extrabold text-[28px] uppercase tracking-[-0.015em] mt-2 leading-tight">
                    {VERTICAL_LABEL[v]} tools
                  </h2>
                  <p className="text-sub text-sm mt-3 max-w-[320px]">
                    {VERTICAL_BLURB[v]}
                  </p>
                  <Link
                    to={`/tools/vertical/${VERTICAL_SLUG[v]}`}
                    className="text-accent font-semibold text-sm mt-3 inline-block"
                  >
                    Browse all {VERTICAL_LABEL[v].toLowerCase()} tools →
                  </Link>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {TOOLS.filter((t) => t.vertical === v).map((t) => (
                    <Link key={t.slug} to={`/tools/${t.slug}`} className="block group">
                      <Card className="h-full hover:shadow-rollup transition-shadow">
                        <div className="px-5 py-5">
                          <div
                            className="w-9 h-9 grid place-items-center rounded-md text-white mb-3"
                            style={{ backgroundColor: VERTICAL_ACCENT[v] }}
                          >
                            <Icon name={t.icon as never} size={16} />
                          </div>
                          <div className="font-display font-extrabold text-[16px] uppercase tracking-[-0.005em]">
                            {t.name}
                          </div>
                          <p className="text-sub text-[13px] mt-2">{t.blurb}</p>
                          <div className="text-accent font-semibold text-sm mt-3">
                            Open tool →
                          </div>
                        </div>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          ))
        )}
      </div>

      <div className="mt-14 max-w-[760px]">
        <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
          Why we built these
        </div>
        <h2 className="t-display-l mt-2">Operators kept asking us the same questions.</h2>
        <p className="text-sub mt-4">
          Most operators run the math by hand — "what's the security-deposit
          cap in my state again?", "if a tenant stops paying on the 15th, when
          can I send the formal notice?", "what's a fair monthly permit price
          for a downtown lot?", "what does a 28-foot slip rent for in my
          market?". The answers live in the same per-state, per-vertical data
          NestPlot's lease engine uses; we surfaced them as free tools so
          anyone can sanity-check their workflow, customer or not.
        </p>
        <p className="text-sub mt-3">
          NestPlot doesn't show you a captcha to use these. No email gate, no
          signup, no drip campaign. If they're useful and you decide you want
          the rest of what NestPlot does — across multifamily, self-storage,
          parking, or RV/boat — the door's open.
        </p>
      </div>
    </div>
  );
}

function SingleVerticalSection({
  vertical,
  tools,
}: {
  vertical: ToolVertical;
  tools: typeof TOOLS;
}) {
  const groups = ["Lease", "Billing", "Operations", "Marketing"] as const;
  return (
    <>
      {groups.map((g) => {
        const items = tools.filter((t) => t.category === g);
        if (items.length === 0) return null;
        return (
          <section key={g}>
            <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase mb-4">
              {g}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((t) => (
                <Link key={t.slug} to={`/tools/${t.slug}`} className="block group">
                  <Card className="h-full hover:shadow-rollup transition-shadow">
                    <div className="px-5 py-5">
                      <div
                        className="w-9 h-9 grid place-items-center rounded-md text-white mb-3"
                        style={{ backgroundColor: VERTICAL_ACCENT[vertical] }}
                      >
                        <Icon name={t.icon as never} size={16} />
                      </div>
                      <div className="font-display font-extrabold text-[17px] uppercase tracking-[-0.005em]">
                        {t.name}
                      </div>
                      <p className="text-sub text-sm mt-2">{t.blurb}</p>
                      <div className="text-accent font-semibold text-sm mt-3">
                        Open tool →
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        );
      })}
    </>
  );
}
